import { useEffect, useLayoutEffect, useState } from 'react';

interface UseNowOptions {
  /** How often the timestamp refreshes, in milliseconds. Defaults to 1000. */
  intervalMs?: number;
  /**
   * When false, no interval runs and the timestamp stays frozen. Flipping to
   * true immediately recomputes a fresh Date.now() before the interval starts.
   */
  enabled?: boolean;
}

/**
 * Returns the current epoch timestamp (ms), refreshed on an interval.
 *
 * The React Compiler memoizes render-time `Date.now()` expressions into a
 * one-shot memo slot — in an always-mounted component the value freezes at
 * app-load time forever (the ENG-5133 "COMPLETED -2145s ago" bug). This hook
 * is the sanctioned alternative: the timestamp lives in state, so the
 * Compiler memoizes correctly and the value ticks via a real interval.
 *
 * WHEN TO USE:
 * - Rendering relative times ("3m ago") that must stay live while visible
 * - Any render-time "current time" need in an always-mounted component
 *
 * WHEN NOT TO USE:
 * - Event handlers or effects — call `Date.now()` directly there (impure
 *   reads are fine outside render)
 * - One-shot timestamps captured at interaction time (e.g. submittedAt)
 *
 * LIMITATION: browsers throttle setInterval in hidden tabs (down to ~1
 * tick/min under Chrome's intensive throttling). Ticks resume on
 * visibility, so coarse relative times (minute granularity) are
 * unaffected — but do not rely on this hook for second-precision counters
 * in backgrounded tabs without adding a visibilitychange reseed.
 *
 * @example
 * // Tick once a second, but only while the popover is open:
 * const nowMs = useNow({ enabled: open, intervalMs: 1000 });
 * return <BulkJobRow nowMs={nowMs} />;
 */
export function useNow({
  intervalMs = 1000,
  enabled = true,
}: UseNowOptions = {}): number {
  const [now, setNow] = useState(() => Date.now());

  // Reseed in a LAYOUT effect: it commits before the browser paints, so
  // re-enabling (e.g. reopening the drawer) shows a fresh timestamp in the
  // first visible frame — a passive effect lands after paint and can flash
  // the value frozen at the previous enable. (Reseeding during render via
  // the derived-state pattern is not an option: `Date.now()` in render
  // violates react-hooks/purity — the ENG-5133 hazard class itself.)
  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNow(Date.now());
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const interval = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => clearInterval(interval);
  }, [enabled, intervalMs]);

  return now;
}
